import React, { useCallback, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";

import { bulkResumeService } from "../services/bulkResumeService";
import { light as C, MONO, radius } from "../../hiring/theme";

// The teal one shade darker, for hover. A local constant rather than a new key
// on the imported token object: `light` is a shared singleton, and assigning to
// it here would silently add the key for every other screen that imports it.
const ACT_HOVER = "#115e59";

// Turning a folder of resumes into candidates.
//
// WHY THIS EXISTS
// Intake assumed a candidate fills a form and attaches a CV. Real hiring often
// starts the other way round — twenty PDFs someone was emailed, or a folder from
// a consultant — and retyping twenty names and phone numbers into a form to get
// them into the pipeline is exactly the work the pipeline is supposed to remove.
//
// WHAT THE RESULT SCREEN IS FOR
// Not celebration. A batch of twenty will contain a scan, a portfolio attached
// by mistake, and somebody already in the pipeline. The three outcomes are shown
// separately because they need different responses: added needs nothing,
// duplicate needs nothing, unreadable needs a human to open the file. Rolling
// them into "17 succeeded" hides the only part that needs work.

const MAX_FILES = 25;
const MAX_BYTES = 15 * 1024 * 1024;
const ACCEPT = ".pdf,.docx,.txt";

const isAllowed = (f) => /\.(pdf|docx|txt)$/i.test(f.name);

function pluralise(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

export default function BulkResumeUpload({ open, onClose, jobId, jobTitle, onDone }) {
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const reset = () => {
    setFiles([]);
    setResult(null);
    setError("");
    setProgress(0);
    setBusy(false);
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const addFiles = useCallback((incoming) => {
    setError("");
    const picked = Array.from(incoming || []);
    const rejected = [];

    const kept = picked.filter((f) => {
      if (!isAllowed(f)) {
        rejected.push(`${f.name} — not a PDF, DOCX or TXT`);
        return false;
      }
      if (f.size > MAX_BYTES) {
        rejected.push(`${f.name} — over 15 MB`);
        return false;
      }
      return true;
    });

    setFiles((prev) => {
      // Same file dropped twice — easy to do when dragging from two folders.
      // Matched on name and size rather than name alone, so two different
      // people who both called their file "resume.pdf" are both kept.
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const fresh = kept.filter((f) => !seen.has(`${f.name}:${f.size}`));
      const next = [...prev, ...fresh];

      if (next.length > MAX_FILES) {
        setError(
          `${MAX_FILES} resumes at a time. Keeping the first ${MAX_FILES} — ` +
            `upload the rest in a second batch.`,
        );
        return next.slice(0, MAX_FILES);
      }
      if (rejected.length) setError(rejected.join(" · "));
      return next;
    });
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const submit = async () => {
    if (!files.length) return;
    setBusy(true);
    setError("");
    setProgress(0);
    try {
      const res = await bulkResumeService.upload(jobId, files, setProgress);
      setResult(res?.data?.data || null);
      onDone?.();
    } catch (err) {
      setError(
        err?.response?.data?.statusMessage ||
          err?.message ||
          "The upload failed. Nothing was added.",
      );
    } finally {
      setBusy(false);
    }
  };

  const added = result?.results?.filter((r) => r.ok && !r.duplicate) || [];
  const dupes = result?.results?.filter((r) => r.duplicate) || [];
  const failed = result?.results?.filter((r) => !r.ok) || [];

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: radius.lg, bgcolor: C.bg } }}
    >
      <DialogContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <Stack direction="row" alignItems="flex-start" sx={{ mb: 2.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 11,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                color: C.faint,
              }}
            >
              {jobTitle || "This job"}
            </Typography>
            <Typography
              sx={{ mt: 0.5, fontSize: 20, fontWeight: 700, color: C.ink, letterSpacing: "-.01em" }}
            >
              Add candidates from resumes
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13.5, color: C.muted, lineHeight: 1.6 }}>
              Drop the files in. Names, emails and phone numbers are read from the
              resumes — nobody has to fill a form.
            </Typography>
          </Box>
          <Button onClick={close} disabled={busy} sx={{ minWidth: 0, p: 0.5, color: C.faint }}>
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </Button>
        </Stack>

        {/* ── Result ─────────────────────────────────────────────────────── */}
        {result ? (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Tally n={added.length} label="added" tone={C.positive} bg={C.positiveSoft} />
              {dupes.length > 0 && (
                <Tally n={dupes.length} label="already here" tone={C.muted} bg={C.panel} />
              )}
              {failed.length > 0 && (
                <Tally n={failed.length} label="unreadable" tone={C.negative} bg={C.negativeSoft} />
              )}
            </Stack>

            {result.screening_started && (
              <Alert
                severity="info"
                sx={{ mb: 2, borderRadius: radius.md, fontSize: 13, bgcolor: C.actSoft, color: C.body }}
              >
                Resume screening is running in the background. Ratings will appear
                on the pipeline in a minute or so.
              </Alert>
            )}

            {/* Credits. Above the consent notice because it is the one that
                means work did not happen, rather than work that needs care. */}
            {result.credit_warning && (
              <Alert
                severity="warning"
                sx={{
                  mb: 2,
                  borderRadius: radius.md,
                  fontSize: 13,
                  bgcolor: C.cautionSoft,
                  color: C.body,
                  "& .MuiAlert-icon": { color: C.caution },
                }}
              >
                {result.credit_warning}
              </Alert>
            )}

            {result.consent_notice && (
              <Alert
                severity="warning"
                sx={{
                  mb: 2,
                  borderRadius: radius.md,
                  fontSize: 13,
                  bgcolor: C.cautionSoft,
                  color: C.body,
                  "& .MuiAlert-icon": { color: C.caution },
                }}
              >
                {result.consent_notice}
              </Alert>
            )}

            <Box sx={{ maxHeight: 300, overflowY: "auto", mb: 2 }}>
              {added.map((r, i) => (
                <Row
                  key={`a${i}`}
                  icon={<CheckCircleRoundedIcon sx={{ fontSize: 17, color: C.positive }} />}
                  title={r.candidate_name}
                  sub={[r.email, r.phone].filter(Boolean).join(" · ") || r.file}
                  flag={r.low_confidence ? "check the name" : null}
                />
              ))}
              {dupes.map((r, i) => (
                <Row
                  key={`d${i}`}
                  icon={<ContentCopyRoundedIcon sx={{ fontSize: 16, color: C.faint }} />}
                  title={r.candidate_name}
                  sub="Already on this job — their resume was updated"
                />
              ))}
              {failed.map((r, i) => (
                <Row
                  key={`f${i}`}
                  icon={<ErrorOutlineRoundedIcon sx={{ fontSize: 17, color: C.negative }} />}
                  title={r.file}
                  sub={r.reason}
                  href={r.resume_url}
                />
              ))}
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Button
                onClick={reset}
                sx={{ textTransform: "none", fontWeight: 650, color: C.act }}
              >
                Upload more
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                onClick={close}
                sx={{
                  textTransform: "none",
                  fontWeight: 650,
                  // `background`, not `bgcolor`. The ATS theme paints
                  // containedPrimary with a gold gradient, and a
                  // background-color cannot override a background-image — the
                  // gradient just paints over it. Setting the shorthand
                  // replaces both.
                  background: C.act,
                  borderRadius: radius.pill,
                  px: 3,
                  boxShadow: "none",
                  "&:hover": { background: ACT_HOVER, boxShadow: "none" },
                }}
              >
                Done
              </Button>
            </Stack>
          </>
        ) : (
          <>
            {/* ── Dropzone ─────────────────────────────────────────────── */}
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => !busy && inputRef.current?.click()}
              sx={{
                border: `1.5px dashed ${dragging ? C.act : C.line}`,
                bgcolor: dragging ? C.actSoft : C.card,
                borderRadius: radius.md,
                p: 4,
                textAlign: "center",
                cursor: busy ? "default" : "pointer",
                transition: "border-color .18s, background-color .18s",
                "&:hover": { borderColor: busy ? C.line : C.act },
              }}
            >
              <UploadFileRoundedIcon sx={{ fontSize: 30, color: dragging ? C.act : C.faint }} />
              <Typography sx={{ mt: 1, fontSize: 14.5, fontWeight: 650, color: C.ink }}>
                Drop resumes here, or click to choose
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 12.5, color: C.muted }}>
                PDF, DOCX or TXT · up to {MAX_FILES} files · 15 MB each
              </Typography>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={ACCEPT}
                hidden
                onChange={(e) => {
                  addFiles(e.target.files);
                  // Cleared so choosing the same file again still fires onChange.
                  e.target.value = "";
                }}
              />
            </Box>

            {error && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: radius.md, fontSize: 12.5 }}>
                {error}
              </Alert>
            )}

            {files.length > 0 && (
              <Box sx={{ mt: 2, maxHeight: 220, overflowY: "auto" }}>
                {files.map((f, i) => (
                  <Stack
                    key={`${f.name}-${i}`}
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    sx={{ py: 0.85, borderBottom: `1px solid ${C.hair}` }}
                  >
                    <DescriptionRoundedIcon sx={{ fontSize: 16, color: C.faint }} />
                    <Typography sx={{ flex: 1, fontSize: 13, color: C.body }} noWrap>
                      {f.name}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: C.faint, fontVariantNumeric: "tabular-nums" }}>
                      {(f.size / 1024).toFixed(0)} KB
                    </Typography>
                    {!busy && (
                      <Button
                        onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}
                        sx={{ minWidth: 0, p: 0.25, color: C.faint }}
                      >
                        <CloseRoundedIcon sx={{ fontSize: 15 }} />
                      </Button>
                    )}
                  </Stack>
                ))}
              </Box>
            )}

            {busy && (
              <Box sx={{ mt: 2.5 }}>
                <LinearProgress
                  variant={progress < 100 ? "determinate" : "indeterminate"}
                  value={progress}
                  sx={{ borderRadius: 99, height: 4 }}
                />
                <Typography sx={{ mt: 1, fontSize: 12.5, color: C.muted }}>
                  {progress < 100
                    ? `Uploading… ${progress}%`
                    : "Reading the resumes — this takes a few seconds each."}
                </Typography>
              </Box>
            )}

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }} alignItems="center">
              <Typography sx={{ fontSize: 13, color: C.muted }}>
                {files.length ? pluralise(files.length, "file", "files") + " ready" : ""}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button onClick={close} disabled={busy} sx={{ textTransform: "none", color: C.muted }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={submit}
                disabled={!files.length || busy}
                startIcon={busy ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : null}
                sx={{
                  textTransform: "none",
                  fontWeight: 650,
                  // `background`, not `bgcolor`. The ATS theme paints
                  // containedPrimary with a gold gradient, and a
                  // background-color cannot override a background-image — the
                  // gradient just paints over it. Setting the shorthand
                  // replaces both.
                  background: C.act,
                  borderRadius: radius.pill,
                  px: 3,
                  boxShadow: "none",
                  "&:hover": { background: ACT_HOVER, boxShadow: "none" },
                }}
              >
                {busy ? "Working…" : "Add candidates"}
              </Button>
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Tally = ({ n, label, tone, bg }) => (
  <Box sx={{ flex: 1, bgcolor: bg, borderRadius: radius.md, px: 2, py: 1.25 }}>
    <Typography sx={{ fontSize: 22, fontWeight: 700, color: tone, lineHeight: 1.1 }}>
      {n}
    </Typography>
    <Typography sx={{ fontSize: 12, color: C.muted }}>{label}</Typography>
  </Box>
);

const Row = ({ icon, title, sub, flag, href }) => (
  <Stack
    direction="row"
    spacing={1.25}
    alignItems="flex-start"
    sx={{ py: 1, borderBottom: `1px solid ${C.hair}` }}
  >
    <Box sx={{ pt: 0.25 }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontSize: 13.5, fontWeight: 650, color: C.ink }} noWrap>
          {title}
        </Typography>
        {flag && (
          <Chip
            size="small"
            label={flag}
            sx={{
              height: 18,
              fontSize: 10.5,
              fontWeight: 650,
              bgcolor: C.cautionSoft,
              color: C.caution,
            }}
          />
        )}
      </Stack>
      <Typography sx={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>
        {sub}
      </Typography>
      {/* The file we could not read is still the file they sent. A link beats
          telling somebody a document failed when they no longer have it open. */}
      {href && (
        <Typography
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: 12, color: C.act, fontWeight: 650, textDecoration: "none" }}
        >
          Open the file →
        </Typography>
      )}
    </Box>
  </Stack>
);
